import { plural, t } from '@lingui/core/macro';
import { GQAssignFacilitiesToTasksMutation } from '@watershed/app-dashboard/generated/graphql-operations';
import { CanonicalDatasetKind } from '@watershed/constants/datasets';

import { Plural, Trans } from '@lingui/react/macro';
import { useAssignFacilitiesToTasksMutation } from '@watershed/app-dashboard/generated/urql';

import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import { useDialogConfirm } from '@watershed/ui-core/components/DialogConfirm';
import invariant from 'invariant';
import uniqBy from 'lodash/uniqBy';
import { OperationResult } from 'urql';

export function useAssignFacilitiesToTasksMutationWithConfirmation() {
  const openAssignFacilitiesDialogConfirm = useDialogConfirm();
  const openLockedTaskDialog = useDialogConfirm();
  const [, executeAssignFacilitiesToTasksMutation] =
    useAssignFacilitiesToTasksMutation();

  return async ({
    userUploadTaskIds,
    measurementProjectId,
    includeListBuildingIds,
  }: {
    userUploadTaskIds: Array<string>;
    measurementProjectId: string;
    includeListBuildingIds: Array<string>;
  }): Promise<
    OperationResult<GQAssignFacilitiesToTasksMutation> | { cancelled: true }
  > => {
    const initialRes = await executeAssignFacilitiesToTasksMutation({
      input: {
        userUploadTaskIds,
        measurementProjectId,
        includeListBuildingIds,
      },
    });

    const facilitiesRequiringConfirmation =
      initialRes.data?.assignFacilitiesToTasks.facilitiesRequiringConfirmation;
    const lockedDestinationTasks =
      initialRes.data?.assignFacilitiesToTasks.lockedDestinationTasks;
    const lockedSourceTasks =
      initialRes.data?.assignFacilitiesToTasks.lockedSourceTasks;

    if (
      !facilitiesRequiringConfirmation?.length &&
      !lockedDestinationTasks?.length &&
      !lockedSourceTasks?.length
    ) {
      // Either we errored or we successfully assigned the facilities -- either way, return the result
      return initialRes;
    }

    // At least one destination task is locked.
    if (lockedDestinationTasks?.length) {
      const msg = plural(lockedDestinationTasks.length, {
        1: `The destination task ${lockedDestinationTasks[0].datasource.name} has been submitted. To add facilities, undo submission or reject task.`,
        2: `The destination tasks ${
          lockedDestinationTasks[0].datasource.name
        } and ${
          lockedDestinationTasks.at(1)?.datasource.name
        } have been submitted. To add facilities, undo submission or reject tasks.`,
        // this shouldn't happen but we needed it as part of plural
        other: `${lockedDestinationTasks[0].datasource.name} and # other destination tasks have been submitted. To add facilities, undo submission or reject tasks.`,
      });

      await openLockedTaskDialog({
        title: t`Facilities could not be added`,
        message: msg,
      });

      return initialRes;
      // At least one source task is locked.
    } else if (lockedSourceTasks?.length) {
      const msg = plural(lockedSourceTasks.length, {
        1: `The source task ${lockedSourceTasks[0].datasource.name} has been submitted. To add facilities, undo submission or reject task.`,
        2: `The source tasks ${lockedSourceTasks[0].datasource.name} and ${
          lockedSourceTasks.at(1)?.datasource.name
        } have been submitted. To add facilities, undo submission or reject tasks.`,
        other: `${lockedSourceTasks[0].datasource.name} and # other source tasks have been submitted. To add facilities, undo submission or reject tasks.`,
      });

      await openLockedTaskDialog({
        title: t`Facilities could not be added`,
        message: msg,
      });
      return initialRes;
      // Some facilities are already assigned to another task. Prompt the user to confirm.
    } else if (facilitiesRequiringConfirmation?.length) {
      let isUtilitiesTask = true;
      let isFacilitiesTask = true;

      // We either have one task or two tasks here. If there are two tasks, that
      // means we are assigning to both a facilities and utilities task. If
      // there is one task, that means we're only assigning to one type.
      if (userUploadTaskIds.length === 1) {
        const tasks =
          flattenConnection(
            initialRes.data?.assignFacilitiesToTasks.measurementProject
              .userUploadTasks
          ) ?? [];
        const theTask = tasks.find((task) => task.id === userUploadTaskIds[0]);
        if (
          theTask?.datasource.dataset.canonicalDataset?.kind ===
          CanonicalDatasetKind.Utilities
        ) {
          isFacilitiesTask = false;
        } else {
          isUtilitiesTask = false;
        }
      } else {
        invariant(
          userUploadTaskIds.length === 2,
          'Must be one facilities task and one utilities task'
        );
      }

      // get unique facilities because if we are assigning to both a facilities and utilities task, they will be duplicated
      const uniqueFacilitiesRequiringConfirmation = uniqBy(
        facilitiesRequiringConfirmation,
        (facility) => facility.id
      );

      const firstBuildingName =
        uniqueFacilitiesRequiringConfirmation[0].buildingName;

      const numFacilitiesWithDataExplanationForFacilitiesTask = plural(
        uniqueFacilitiesRequiringConfirmation.length,
        {
          offset: 1,
          1: `Facilities data will transfer, but uploaded files will remain in prior task. All the facilities data for ${firstBuildingName} will move to the new facilities task.`,
          2: `Facilities data will transfer, but uploaded files will remain in prior task. All the facilities data for ${firstBuildingName} and 1 other facility will move to the new facilities task.`,
          other: `Facilities data will transfer, but uploaded files will remain in prior task. All the facilities data for ${firstBuildingName} and # other facilities will move to the new facilities task.`,
        }
      );

      const numFacilitiesWithDataExplanationForUtilitiesTask = plural(
        uniqueFacilitiesRequiringConfirmation.length,
        {
          offset: 1,
          1: `${firstBuildingName} already has data in an existing utilities task.`,
          2: `${firstBuildingName} and 1 other facility already have data in an existing utilities task.`,
          other: `${firstBuildingName} and # other facilities already have data in an existing utilities task.`,
        }
      );

      if (isFacilitiesTask) {
        const didConfirmFacilitiesTask =
          await openAssignFacilitiesDialogConfirm({
            title: (
              <Plural
                value={uniqueFacilitiesRequiringConfirmation.length}
                one="Reassign 1 facility?"
                other="Reassign # facilities?"
              />
            ),
            message: `${numFacilitiesWithDataExplanationForFacilitiesTask} Any uploaded files will remain in the existing facilities task.`,
            confirmText: (
              <Trans context="button to confirm assigning facilities to tasks">
                Confirm
              </Trans>
            ),
            confirmColor: 'primary',
          });
        if (!didConfirmFacilitiesTask) {
          return { cancelled: true };
        }
      }
      if (isUtilitiesTask) {
        const didConfirmUtilitiesTask = await openAssignFacilitiesDialogConfirm(
          {
            title: (
              <Plural
                value={uniqueFacilitiesRequiringConfirmation.length}
                one="Reassign 1 facility?"
                other="Reassign # facilities?"
              />
            ),
            message: `${numFacilitiesWithDataExplanationForUtilitiesTask} If you continue, any data that was added in the existing task won't transfer to the new utilities task. Any data from previous measurements will remain.`,
            confirmText: (
              <Trans context="Confirmation button text to remove a facility from a task and add it to another task">
                Re-assign and abandon data
              </Trans>
            ),
            confirmColor: 'error',
          }
        );
        if (!didConfirmUtilitiesTask) {
          return { cancelled: true };
        }
      }

      const secondRes = await executeAssignFacilitiesToTasksMutation({
        input: {
          userUploadTaskIds,
          measurementProjectId,
          includeListBuildingIds,
          allowReassignment: true, // Allow reassignment now that user has confirmed
        },
      });
      return secondRes;
    } else {
      return initialRes;
    }
  };
}
